import type { UserProgress } from '@/hooks/useProgress';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'offense' | 'defense' | 'rules' | 'general' | 'tactics' | 'setpieces';
  sport: 'football' | 'soccer' | 'both';
  check: (progress: UserProgress) => boolean;
}

// Helper: check if all lessons in a list are completed
function hasAllLessons(completed: string[], required: string[]): boolean {
  return required.every(id => completed.includes(id));
}

export const achievements: Achievement[] = [
  // ─── General (both sports) ───
  {
    id: 'first-snap',
    title: 'First Snap',
    description: 'Complete your first lesson',
    icon: '🎯',
    category: 'general',
    sport: 'both',
    check: (p) => p.completedLessons.length >= 1,
  },
  {
    id: 'hat-trick',
    title: 'Hat Trick',
    description: 'Complete 3 lessons',
    icon: '🎩',
    category: 'general',
    sport: 'both',
    check: (p) => p.completedLessons.length >= 3,
  },
  {
    id: 'film-junkie',
    title: 'Film Junkie',
    description: 'Complete 10 lessons',
    icon: '📺',
    category: 'general',
    sport: 'both',
    check: (p) => p.completedLessons.length >= 10,
  },
  {
    id: 'playbook-scholar',
    title: 'Playbook Scholar',
    description: 'Complete 20 lessons',
    icon: '📖',
    category: 'general',
    sport: 'both',
    check: (p) => p.completedLessons.length >= 20,
  },
  {
    id: 'streak-starter',
    title: 'Streak Starter',
    description: 'Reach a 3-day streak',
    icon: '🔥',
    category: 'general',
    sport: 'both',
    check: (p) => p.streak >= 3,
  },
  {
    id: 'on-fire',
    title: 'On Fire',
    description: 'Reach a 7-day streak',
    icon: '🌋',
    category: 'general',
    sport: 'both',
    check: (p) => p.streak >= 7,
  },
  {
    id: 'century-club',
    title: 'Century Club',
    description: 'Earn 100 XP',
    icon: '💯',
    category: 'general',
    sport: 'both',
    check: (p) => p.xp >= 100,
  },
  {
    id: 'xp-machine',
    title: 'XP Machine',
    description: 'Earn 500 XP',
    icon: '⚡',
    category: 'general',
    sport: 'both',
    check: (p) => p.xp >= 500,
  },
  {
    id: 'perfect-game',
    title: 'Perfect Game',
    description: 'Get 100% on any lesson',
    icon: '⭐',
    category: 'general',
    sport: 'both',
    check: (p) => Object.values(p.lessonScores).some(s => s.correct === s.total && s.total > 0),
  },
  {
    id: 'scheme-master',
    title: 'Scheme Master',
    description: 'Reach the Scheme Master level',
    icon: '👑',
    category: 'general',
    sport: 'both',
    check: (p) => p.level >= 4,
  },

  // ─── Football: Offense ───
  {
    id: 'formation-guru',
    title: 'Formation Guru',
    description: 'Complete all Formations 101 lessons',
    icon: '📐',
    category: 'offense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['what-is-formation', 'shotgun-vs-undercenter', 'personnel-groups']),
  },
  {
    id: 'rpo-master',
    title: 'RPO Master',
    description: 'Complete Play-Action & RPO lessons',
    icon: '🎭',
    category: 'offense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['play-action-101', 'rpo-basics']),
  },
  {
    id: 'route-runner',
    title: 'Route Runner',
    description: 'Complete all route concept lessons',
    icon: '🏃',
    category: 'offense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['mesh-concept', 'flood-concept']),
  },
  {
    id: 'ground-pounder',
    title: 'Ground & Pound',
    description: 'Complete Power and Counter run lessons',
    icon: '💪',
    category: 'offense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['power-run', 'counter-run']),
  },
  {
    id: 'deep-threat',
    title: 'Deep Threat',
    description: 'Complete 4 Verticals and Y-Cross lessons',
    icon: '🚀',
    category: 'offense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['four-verts', 'y-cross']),
  },

  // ─── Football: Defense ───
  {
    id: 'coverage-expert',
    title: 'Coverage Expert',
    description: 'Complete Cover 1, 2, and 3 lessons',
    icon: '🛡️',
    category: 'defense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['cover-1', 'cover-2', 'cover-3']),
  },
  {
    id: 'zone-defense-expert',
    title: 'Zone Defense Expert',
    description: 'Complete all coverage lessons including Cover 4',
    icon: '🏰',
    category: 'defense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['cover-1', 'cover-2', 'cover-3', 'cover-4']),
  },
  {
    id: 'blitz-happy',
    title: 'Blitz Happy',
    description: 'Complete the blitz package lesson',
    icon: '⚡',
    category: 'defense',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['blitz-basics']),
  },

  // ─── Football: Rules ───
  {
    id: 'rules-scholar',
    title: 'Rules Scholar',
    description: 'Complete all game structure lessons',
    icon: '📋',
    category: 'rules',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['downs-system', 'scoring', 'game-clock']),
  },
  {
    id: 'flag-expert',
    title: 'Flag Expert',
    description: 'Complete all penalty lessons',
    icon: '🚩',
    category: 'rules',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['pre-snap-penalties', 'in-play-penalties', 'special-rules']),
  },
  {
    id: 'red-zone-specialist',
    title: 'Red Zone Specialist',
    description: 'Complete all Red Zone lessons',
    icon: '🎯',
    category: 'rules',
    sport: 'football',
    check: (p) => hasAllLessons(p.completedLessons, ['red-zone-basics', 'red-zone-concepts', 'red-zone-defense']),
  },

  // ─── Soccer: Tactics ───
  {
    id: 'soccer-formation-master',
    title: 'Formation Master',
    description: 'Complete all formation lessons',
    icon: '📐',
    category: 'tactics',
    sport: 'soccer',
    check: (p) => hasAllLessons(p.completedLessons, ['soccer-what-is-formation', 'soccer-442', 'soccer-433']),
  },
  {
    id: 'soccer-pitch-iq',
    title: 'Pitch IQ',
    description: 'Complete all position lessons',
    icon: '🧠',
    category: 'tactics',
    sport: 'soccer',
    check: (p) => hasAllLessons(p.completedLessons, ['soccer-goalkeeper', 'soccer-midfield']),
  },
  {
    id: 'soccer-press-resistant',
    title: 'Press Resistant',
    description: 'Complete all pressing & gegenpressing lessons',
    icon: '🔄',
    category: 'tactics',
    sport: 'soccer',
    check: (p) => hasAllLessons(p.completedLessons, ['soccer-pressing-basics', 'soccer-gegenpressing']),
  },

  // ─── Soccer: Set Pieces ───
  {
    id: 'soccer-set-piece-specialist',
    title: 'Set Piece Specialist',
    description: 'Complete all set piece lessons',
    icon: '🥅',
    category: 'setpieces',
    sport: 'soccer',
    check: (p) => hasAllLessons(p.completedLessons, ['soccer-corners-fks', 'soccer-penalty-kicks']),
  },
  {
    id: 'soccer-penalty-expert',
    title: 'Penalty Expert',
    description: 'Complete the penalty kicks lesson',
    icon: '⚽',
    category: 'setpieces',
    sport: 'soccer',
    check: (p) => hasAllLessons(p.completedLessons, ['soccer-penalty-kicks']),
  },
];

export function getAchievementsForSport(sport: string): Achievement[] {
  return achievements.filter(a => a.sport === 'both' || a.sport === sport);
}

export function getEarnedAchievements(progress: UserProgress): Achievement[] {
  return achievements.filter(a => a.check(progress));
}
